import { PlainLink } from 'components/link';

import { TitleWorkspacesPopover } from './title-workspaces-popover';
import styles from './participation-links.module.scss';

interface Props {
  // The entity the counts/links pertain to. For a collapsed alias family this is
  // the row's display entity — per INS-11025 the links resolve to this Entity ID
  // only, never the family.
  entityId: number;
  entityName: string;
  // The entity's own title workspaces; length is the displayed count and the list
  // feeds the popup.
  titleWorkspaces?: Array<{ id: number; name: string }>;
  interpretationCount?: number;
}

// Source context the destination pages read to filter to this entity, show a
// "filtered to <name>" banner, and prefill their search field (INS-11025).
const sourceParams = (entityId: number, entityName: string) =>
  `exactEntityId=${entityId}&exactEntityName=${encodeURIComponent(entityName)}`;

// "Title Workspaces: N | Interpretations: M" shown beneath an entity's addresses
// in the reconcile tearsheet. The pair is always shown in tandem, bar-delimited,
// and only when at least one side is non-zero (both-zero renders nothing). A zero
// side is muted, display-only (no link). The Title Workspaces count opens a popup
// of workspace links (each → that workspace's Runsheet filtered to this entity);
// the Interpretations count opens the entity's Profile Interpretations tab
// filtered to this entity. Everything filters to this Entity ID only (INS-11025).
export const ParticipationLinks = ({
  entityId,
  entityName,
  titleWorkspaces,
  interpretationCount = 0,
}: Props) => {
  const workspaces = titleWorkspaces ?? [];

  if (workspaces.length === 0 && interpretationCount === 0) return null;

  const params = sourceParams(entityId, entityName);

  return (
    <div className={styles.pair}>
      {workspaces.length > 0 ? (
        <TitleWorkspacesPopover
          workspaces={workspaces}
          runsheetHref={(workspaceId) =>
            `/title_workspaces/${workspaceId}/runsheet?${params}`
          }
        />
      ) : (
        <span className={styles.muted}>Title Workspaces: 0</span>
      )}

      <span className={styles.divider} aria-hidden="true">
        |
      </span>

      {interpretationCount > 0 ? (
        <PlainLink
          href={`/entities/${entityId}?tab=interpretations&${params}`}
          openInNewTab
          // The row navigates on click; without this the row handler hijacks
          // the link and the destination opens in this tab instead of a new one.
          onClick={(event) => event.stopPropagation()}
        >
          Interpretations: {interpretationCount}
        </PlainLink>
      ) : (
        <span className={styles.muted}>Interpretations: 0</span>
      )}
    </div>
  );
};
